import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AppImage from '../../components/AppImage';
import { supabase } from '../../lib/supabase';

const GalleryAccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const galleryCode = searchParams?.get('code');
  const galleryId = searchParams?.get('id');

  const [codeInput, setCodeInput] = useState(galleryCode || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [galleryPreview, setGalleryPreview] = useState(null);

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      
      if (session?.user) {
        // User is authenticated, check their role
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('role')
          ?.eq('id', session?.user?.id)
          ?.single();
        
        if (profile?.role === 'user') {
          // If gallery code/id provided, redirect to that gallery
          if (galleryCode || galleryId) {
            await handleGalleryAccess(galleryCode || galleryId, true);
          } else {
            // Otherwise go to user dashboard
            navigate('/user-dashboard');
          }
        }
      }
    };

    checkAuthAndRedirect();
  }, [galleryCode, galleryId]);

  // Load gallery preview if code/id provided
  useEffect(() => {
    if (galleryCode || galleryId) {
      loadGalleryPreview(galleryCode || galleryId);
    }
  }, [galleryCode, galleryId]);

  const loadGalleryPreview = async (identifier) => {
    try {
      const query = supabase
        ?.from('galleries')
        ?.select('id, gallery_name, description, gallery_type, client_name, creator_id, user_profiles(full_name)')
        ?.eq('is_published', true);

      // Check if identifier is UUID (gallery_id) or code
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(identifier);
      
      if (isUUID) {
        query?.eq('id', identifier);
      } else {
        query?.eq('gallery_code', identifier?.toUpperCase());
      }

      const { data, error } = await query?.single();

      if (error || !data) {
        setError('Gallery not found or not published');
        return;
      }

      setGalleryPreview(data);
    } catch (err) {
      console.error('Error loading gallery preview:', err);
    }
  };

  const handleGalleryAccess = async (code, isAuthenticated = false) => {
    if (!code?.trim()) {
      setError('Please enter a gallery code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Verify gallery exists and is published
      const { data: gallery, error: galleryError } = await supabase
        ?.from('galleries')
        ?.select('id, gallery_name, is_published')
        ?.eq('gallery_code', code?.toUpperCase())
        ?.eq('is_published', true)
        ?.single();

      if (galleryError || !gallery) {
        setError('Invalid gallery code or gallery not available');
        setIsLoading(false);
        return;
      }

      if (isAuthenticated) {
        // User is logged in, redirect to gallery viewer
        navigate(`/gallery-viewer?galleryId=${gallery?.id}`);
      } else {
        // User needs to login/register first
        navigate(`/user-registration?returnUrl=/gallery-viewer?galleryId=${gallery?.id}`);
      }
    } catch (err) {
      console.error('Error accessing gallery:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    handleGalleryAccess(codeInput);
  };

  return (
    <>
      <Helmet>
        <title>Gallery Access - Radiavision Studio</title>
        <meta name="description" content="Access your gallery using a unique code or invitation link" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>

        {/* Header */}
        <header className="relative z-10 border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <AppImage
                  src="/assets/images/RVS_InitialLogo_01-02-1768324259986.png"
                  alt="RVS Logo"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <span className="text-xl font-bold">Radiavision Studio</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="relative z-10 container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-2xl mx-auto">
            {/* Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Icon name="Key" size={40} color="var(--color-primary)" />
              </div>
            </div>

            {/* Heading */}
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-5xl font-bold mb-4">
                Access Your Gallery
              </h1>
              <p className="text-lg text-muted-foreground">
                Enter your unique gallery code to view your photos and videos
              </p>
            </div>

            {/* Gallery Preview */}
            {galleryPreview && (
              <div className="mb-8 p-6 rounded-2xl bg-card border border-border">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Image" size={24} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{galleryPreview?.gallery_name}</h3>
                    {galleryPreview?.description && (
                      <p className="text-sm text-muted-foreground mb-3">{galleryPreview?.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={16} />
                        {galleryPreview?.user_profiles?.full_name || 'Contributor'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Tag" size={16} />
                        {galleryPreview?.gallery_type}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Access Form */}
            <div className="p-8 rounded-3xl bg-card border border-border shadow-warm-lg">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Gallery Code
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your gallery code (e.g., ABC123)"
                    value={codeInput}
                    onChange={(e) => {
                      setCodeInput(e?.target?.value?.toUpperCase());
                      setError('');
                    }}
                    error={error}
                    className="text-center text-lg tracking-wider font-mono"
                  />
                  <p className="mt-2 text-xs text-muted-foreground text-center">
                    Gallery codes are case-insensitive and typically 6-8 characters
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  loading={isLoading}
                >
                  <Icon name="ArrowRight" size={20} className="mr-2" />
                  Access Gallery
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Don't have a gallery code?
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => navigate('/user-registration')}
                    variant="outline"
                    className="flex-1"
                  >
                    <Icon name="UserPlus" size={18} className="mr-2" />
                    Sign Up
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    variant="ghost"
                    className="flex-1"
                  >
                    <Icon name="Home" size={18} className="mr-2" />
                    Back to Home
                  </Button>
                </div>
              </div>
            </div>

            {/* Help Text */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                Gallery codes are provided by your photographer or event organizer.
                <br />
                Check your email, WhatsApp, or invitation message for your code.
              </p>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default GalleryAccess;